import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  TableContainer,
  Paper,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { saveAs } from 'file-saver';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../styles/UserStyles.css';
import {
  ADD_STAFF,
  EDIT_STAFF,
  VIEW_STAFF,
} from '../../component/common/Routes/AppRoutes';
import { columnFetcher, formatDateWithAmPm, getStatusClass } from '../../Style';
import SearchAndButtonTab from '../../component/ui/SearchAndButtonTab';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetStaffDetail,
  staffDeleteRequest,
  staffFetchAllRequest,
  staffViewAllRequest,
} from '../../redux/Staff/StaffAction';
import { fetchRolesRequest } from '../../redux/Roles/actions/RolesActions';
import { toast, ToastContainer } from 'react-toastify';
import CustomButton from '../../component/ui/FormFields/Button';
import nodata from '../../assets/nodata.jpg';
import Loading from '../../component/ui/Loading';
import axiosInstance from '../../redux/instance/AxiosInstance';

const StaffManagementList = () => {
   const location = useLocation();
       
        const pageNumber= location.state?.page ?? 1;
  const user = useSelector((state) => state.auth.user);
  const permissions = useMemo(() => {
    let safePermissions = {};

    try {
      if (user?.permissions) {
        safePermissions =
          typeof user.permissions === 'string'
            ? JSON.parse(user.permissions)
            : user.permissions;
      }
    } catch (error) {
      safePermissions = {};
    }

    return safePermissions;
  }, [user]);

  const navigate = useNavigate();

  const columns = [
    { value: 'staff_serial_no', label: 'Serial No' },
    { value: 'full_name', label: 'Full Name' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Mobile' },
    { value: 'gender', label: 'Gender' },
    { value: 'role', label: 'Role' },
  ];

  const [searchQuery, setSearchQuery] = useState('');
  const [searchColumn, setSearchColumn] = useState('');
  const [open, setOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [page, setPage] = useState(pageNumber);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const searchTimeoutRef = useRef(null);

  const dispatch = useDispatch();
  const rowsPerPage = 10;

  const staffsDetails = useSelector(
    (state) => state.staffs?.StaffsDetails || {}
  );

  const viewListLoading = staffsDetails?.loading;
  const staffDeleteDetails = useSelector((state) => state.staffs?.Staff || {});
  const deleteLoading = staffDeleteDetails?.deleteLoading;

  const staffsValue = staffsDetails?.data || [];
  const totalRecords = staffsDetails?.totalRecords || 0;
  const totalPages = staffsDetails?.totalPages || 1;
  const deleteSuccessMessage = staffDeleteDetails?.success;

  const fetchStaffData = (
    pageNumber,
    search = searchQuery,
    searchBy = searchColumn
  ) => {
    const params = {};
    if (search) {
      params.pagination_required = true;
      params.pagesize = rowsPerPage;
      params.pageNo = pageNumber;
      params.search = search;

      if (searchBy && searchBy.trim() !== '') {
        params.search_by = searchBy;
      }
    } else {
      params.pagination_required = true;
      params.pagesize = rowsPerPage;
      params.pageNo = pageNumber;
    }

    dispatch(staffViewAllRequest(params));
  };

  // Single useEffect for initial load and page changes
  useEffect(() => {
    if (isInitialLoad) {
      dispatch(resetStaffDetail());
      fetchStaffData(1, '', '');
      setIsInitialLoad(false);
    } else {
      fetchStaffData(page, searchQuery, searchColumn);
    }
  }, [page, dispatch]); // Only depend on page and dispatch

  const roles = useSelector((state) => state.roles?.roles || {});

  useEffect(() => {
    dispatch(fetchRolesRequest());
  }, [dispatch]);

  const tableHeaderMapping = {
    'Serial No': 'staff_serial_no',
    'Full Name': 'full_name',
    Email: 'email',
    Mobile: 'phone',
    Gender: 'gender_type',
    DOB: 'dob',
    Role: 'role_id',
    'Last Active': 'last_active',
    'Created Date': 'created_at',
    Status: 'status',
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const generateTableHeaders = () => {
    const baseHeaders = [
      'Staff Id',
      'Full Name',
      'Email',
      'Mobile',
      'Gender',
      'DOB',
      'Role',
      'Last Active',
      'Created Date',
      'Status',
    ];
    const hasStaffActions =
      user?.user_type === 1 ||
      permissions?.staffs?.view ||
      permissions?.staffs?.edit ||
      permissions?.staffs?.delete;
    return hasStaffActions ? [...baseHeaders, 'Actions'] : baseHeaders;
  };

  const tableHeaders = generateTableHeaders();
  const renderSortIcon = (header) => {
    const property = tableHeaderMapping[header];
    if (!property || orderBy !== property) {
      return null;
    }
    return order === 'asc' ? (
      <ArrowUpwardIcon fontSize='small' style={{ marginLeft: '5px' }} />
    ) : (
      <ArrowDownwardIcon fontSize='small' style={{ marginLeft: '5px' }} />
    );
  };

  useEffect(() => {
    if (deleteSuccessMessage) {
      toast.success('Staff Deleted Successfully', {
        position: 'top-center',
        autoClose: 3000,
        onClose: () => {
          dispatch(resetStaffDetail());
          fetchStaffData(1);
        },
      });
    }
  }, [deleteSuccessMessage]);

  const handleSearch = (query, column) => {
    setSearchQuery(query);
    if (column) {
      setSearchColumn(column);
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchStaffData(1, query, column || searchColumn);
    }, 1000);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleExport = () => {
    const params = {
      pagination_required: false,
    };
    if (searchQuery) {
      params.pagination_required = false;
      params.search = searchQuery;

      if (searchColumn && searchColumn.trim() !== '') {
        params.search_by = searchColumn;
      }
    } else {
      params.pagination_required = false;
    }
    const apiCall = axiosInstance.get('/staffs/list', { params });

    apiCall
      .then((response) => {
        const allStaffsData = response?.data?.data || [];
        const csvHeaders = [
          'Staff No,Full Name,Email,Mobile,Gender,Role,Address1,Address2,City,State,Country,Last Active,Created Date,Status',
        ];

        const csvRows = allStaffsData.map((user) => {
          const escapeCsvField = (field) => {
            if (field === null || field === undefined) return '""';
            const stringField = String(field || '');
            if (
              stringField.includes(',') ||
              stringField.includes('"') ||
              stringField.includes('\n')
            ) {
              return `"${stringField.replace(/"/g, '""')}"`;
            }
            return stringField;
          };

          const gender =
            user.gender_type === 'M'
              ? 'Male'
              : user.gender_type === 'F'
              ? 'Female'
              : 'Other';

          const roleName = roleNameFinder(user.role_id);
          const status = user.status == '1' ? 'Active' : 'Inactive';

          return [
            escapeCsvField(user.staff_serial_no),
            escapeCsvField(`${user.first_name} ${user.last_name}`),
            escapeCsvField(user.email),
            escapeCsvField(user.phone),
            escapeCsvField(gender),
            escapeCsvField(roleName),
            escapeCsvField(user.address1),
            escapeCsvField(user.address2),
            escapeCsvField(user.city_name),
            escapeCsvField(user.state_name),
            escapeCsvField(user.country_name),
            escapeCsvField(formatDateWithAmPm(user.last_active)),
            escapeCsvField(formatDateWithAmPm(user.created_at)),
            escapeCsvField(status),
          ].join(',');
        });

        const csvContent = [csvHeaders, ...csvRows].join('\n');
        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        saveAs(blob, 'staffs.csv');
      })
      .catch((error) => {
        console.error('Export error:', error);
      });
  };

  const handleOpenPopup = (user) => {
    setUserToDelete(user);
    setOpen(true);
  };

  const handleClosePopup = () => {
    setOpen(false);
  };

  const roleNameFinder = (id) => {
    const role = roles.find((role) => role.id === id);
    return role ? role.name : 'Unknown Role';
  };

  const sortedStaffs = useMemo(() => {
    if (!orderBy || !staffsValue.length) return staffsValue;

    return [...staffsValue].sort((a, b) => {
      let valA, valB;

      if (orderBy === 'full_name') {
        valA = `${a.first_name} ${a.last_name}`.toLowerCase();
        valB = `${b.first_name} ${b.last_name}`.toLowerCase();
      } else if (orderBy === 'gender_type') {
        valA =
          a.gender_type === 'M'
            ? 'Male'
            : a.gender_type === 'F'
            ? 'Female'
            : 'Other';
        valB =
          b.gender_type === 'M'
            ? 'Male'
            : b.gender_type === 'F'
            ? 'Female'
            : 'Other';
        valA = valA.toLowerCase();
        valB = valB.toLowerCase();
      } else if (orderBy === 'role_id') {
        valA = roleNameFinder(a.role_id).toLowerCase();
        valB = roleNameFinder(b.role_id).toLowerCase();
      } else if (
        orderBy === 'created_at' ||
        orderBy === 'last_active' ||
        orderBy === 'dob'
      ) {
        valA = new Date(a[orderBy] || 0);
        valB = new Date(b[orderBy] || 0);
      } else if (orderBy === 'status') {
        valA = Number(a[orderBy] || 0);
        valB = Number(b[orderBy] || 0);
      } else {
        valA = (a[orderBy] || '').toString().toLowerCase();
        valB = (b[orderBy] || '').toString().toLowerCase();
      }

      if (
        orderBy === 'created_at' ||
        orderBy === 'last_active' ||
        orderBy === 'dob'
      ) {
        return order === 'asc' ? valA - valB : valB - valA;
      } else if (typeof valA === 'number' && typeof valB === 'number') {
        return order === 'asc' ? valA - valB : valB - valA;
      } else {
        if (valA < valB) return order === 'asc' ? -1 : 1;
        if (valA > valB) return order === 'asc' ? 1 : -1;
        return 0;
      }
    });
  }, [staffsValue, orderBy, order, roles]);

  const handleConfirmDelete = async (deleteValue) => {
    await dispatch(staffDeleteRequest(deleteValue.id));
    handleClosePopup();
  };

  const renderActions = (userItem) => {
    const hasAnyAction =
      user?.user_type === 1 ||
      permissions?.staffs?.view ||
      permissions?.staffs?.edit ||
      permissions?.staffs?.delete;

    if (!hasAnyAction) return null;

    return (
      <div className='button-list'>
        {(user?.user_type === 1 || permissions?.staffs?.view) && (
          <Button
            variant='contained'
            size='small'
            className='view-user-button'
            onClick={() =>
              navigate(`${VIEW_STAFF.replace(':staffId', userItem.id)}`,{state:{page:page}})
            }
          >
            VIEW
          </Button>
        )}
        {(user?.user_type === 1 || permissions?.staffs?.edit) && (
          <Button
            variant='contained'
            size='small'
            className='edit-user-button'
            onClick={() =>
              navigate(`${EDIT_STAFF.replace(':staffId', userItem.id)}`,{state:{pageEdit:page}})
            }
          >
            EDIT
          </Button>
        )}
        {(user?.user_type === 1 || permissions?.staffs?.delete) && (
          <CustomButton
            variant='contained'
            className='delete-user-button'
            type='button'
            text={'DELETE'}
            size='small'
            onClick={() => handleOpenPopup(userItem)}
          />
        )}
      </div>
    );
  };

  const handleIdClick = (userId) => {
    if (user?.user_type === 1 || permissions?.staffs?.view) {
      navigate(`${VIEW_STAFF.replace(':staffId', userId)}`);
    }
  };

  if (viewListLoading) {
    return <Loading text='Loading Staff details...' />;
  }

  return (
    <>
      <ToastContainer />
      <Box>
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <SearchAndButtonTab
                  title='Staffs'
                  columns={columns}
                  hideSearchColumnSelector={true}
                  searchColumn={searchColumn}
                  setSearchColumn={setSearchColumn}
                  searchQuery={searchQuery}
                  setSearchQuery={handleSearch}
                  handleExport={handleExport}
                  routeName={ADD_STAFF}
                  permissions={permissions}
                  user={user}
                  type='staffs'
                  searchPlaceholder='Search by Serial No, Name, Email or Mobile'
                />
                {sortedStaffs.length > 0 ? (
                  <TableContainer component={Paper} sx={{ marginTop: '16px' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {tableHeaders.map((header) => (
                            <TableCell
                              key={header}
                              onClick={() => {
                                if (tableHeaderMapping[header]) {
                                  handleSort(tableHeaderMapping[header]);
                                }
                              }}
                              style={
                                tableHeaderMapping[header]
                                  ? { cursor: 'pointer', userSelect: 'none' }
                                  : {}
                              }
                            >
                              <Box display='flex' alignItems='center'>
                                {header}
                                {renderSortIcon(header)}
                              </Box>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedStaffs.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              {user?.user_type === 1 ||
                              permissions?.staffs?.view ? (
                                <span
                                  onClick={() => handleIdClick(user.id)}
                                  style={{
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                  }}
                                >
                                  {user.staff_unique_id}
                                </span>
                              ) : (
                                user.staff_unique_id
                              )}
                            </TableCell>
                            <TableCell>{`${user.first_name} ${user.last_name}`}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.phone}</TableCell>
                            <TableCell>
                              {user.gender_type === 'M'
                                ? 'Male'
                                : user.gender_type === 'F'
                                ? 'Female'
                                : 'Other'}
                            </TableCell>
                            <TableCell>
                              {formatDateWithAmPm(user.dob)}
                            </TableCell>
                            <TableCell>
                              <span
                                className={`badge ${getStatusClass(
                                  roleNameFinder(user.role_id)
                                )}`}
                              >
                                {roleNameFinder(user.role_id)}
                              </span>
                            </TableCell>
                            <TableCell>
                              {formatDateWithAmPm(user.last_active)}
                            </TableCell>
                            <TableCell>
                              {formatDateWithAmPm(user.created_at)}
                            </TableCell>
                            <TableCell>
                              <span
                                className={`badge ${getStatusClass(
                                  user.status === 1 ? 'Active' : 'Inactive'
                                )}`}
                              >
                                {user.status === 1 ? 'Active' : 'Inactive'}
                              </span>
                            </TableCell>
                            {tableHeaders.includes('Actions') && (
                              <TableCell>{renderActions(user)}</TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    width='100%'
                    py={5}
                    mt={2}
                  >
                    <img
                      src={nodata}
                      alt='No Data'
                      style={{
                        maxWidth: '300px',
                        maxHeight: '300px',
                      }}
                    />
                  </Box>
                )}
                {sortedStaffs.length > 0 && (
                  <Box className='button-box'>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color='primary'
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Dialog open={open} onClose={handleClosePopup}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete{' '}
              <strong>
                {userToDelete?.first_name} {userToDelete?.last_name}
              </strong>
              ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleClosePopup}
              variant='contained'
              className='grey-button'
            >
              Cancel
            </Button>
            <CustomButton
              variant='contained'
              className='orange-button'
              type='button'
              loading={deleteLoading}
              text={'DELETE'}
              onClick={() => handleConfirmDelete(userToDelete)}
            />
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default StaffManagementList;
